/**
 * components/CostItemsEditor.js
 * The "Rincian Tambahan" card (was "Biaya & Tagihan" — renamed in the corrective pass's Piece
 * 2, once a Potongan row made the old title inaccurate; see TransactionDetailModal.js's own
 * header comment for the full reasoning) — an itemized list of extra cost rows on a
 * transaction, separate from its goods lines (Brief 1 of the cost-items rollout).
 * Presentational only: the parent (`TransactionFormModal`) owns `items`/`onChange` state and
 * the `validateCostItems` check, mirroring the exact relationship that component already has
 * with `payments`/`paymentsCheck`.
 *
 * Rows are EDITABLE IN PLACE (description, amount, AND type all changeable after a row is
 * added — confirmed with Chalton during planning, overriding the type-locked-after-add
 * framing from the original brief) — reuses TransactionFormModal's own payment-rows editor
 * convention (index-keyed setters, continuous validation gating Simpan) rather than a
 * read-only/remove-and-re-add design. The add buttons only lock in a NEW row's *initial* type
 * at the moment of adding, so a just-added row is never in an ambiguous state. The button row
 * renders generically from `costItemTypesFor(txnType)`: since 2026-10-11 (the cost system the
 * client confirmed) that is just "+ Tagihan" and "− Potongan" — what the other side owes or is
 * let off. Our own costs are no longer a row type here; they live in the separate Biaya
 * Lain-Lain box (BiayaLainEditor.js, saved to data.expenses).
 */
import React from "react";
import { formatCurrencyInput, parseCurrencyInput } from "../utils/currency";
import { costItemTypesFor } from "../utils/costItems";

export default function CostItemsEditor({ items, onChange, txnType, checkResult }) {
  const typeOptions = costItemTypesFor(txnType);

  const addItem = (type) => onChange([...items, { description: "", amount: null, type }]);
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const setDescription = (i, description) =>
    onChange(items.map((it, idx) => (idx === i ? { ...it, description } : it)));
  const setAmount = (i, amount) =>
    onChange(items.map((it, idx) => (idx === i ? { ...it, amount } : it)));
  const setType = (i, type) =>
    onChange(items.map((it, idx) => (idx === i ? { ...it, type } : it)));

  return (
    <div className="card">
      <div className="card__title">Rincian Tambahan</div>
      {items.length === 0 && <div className="form-hint" style={{ marginBottom: "var(--gap-sm)" }}>Belum ada baris.</div>}
      {items.map((it, i) => (
        <div className="cost-item-row" key={it.id ?? i} style={{ marginBottom: "var(--gap-sm)" }}>
          <div style={{ display: "flex", gap: "var(--gap-md)", alignItems: "flex-end" }}>
            <div className="form-row" style={{ flex: "0 0 auto", marginBottom: 0 }}>
              <label className="form-label" htmlFor={`cost-item-tipe-${i}`}>Tipe</label>
              <select id={`cost-item-tipe-${i}`} className="form-select" value={it.type} onChange={(e) => setType(i, e.target.value)}>
                {typeOptions.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label" htmlFor={`cost-item-deskripsi-${i}`}>Deskripsi</label>
              <input id={`cost-item-deskripsi-${i}`} className="form-input" value={it.description}
                onChange={(e) => setDescription(i, e.target.value)} />
            </div>
            <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label" htmlFor={`cost-item-jumlah-${i}`}>Jumlah</label>
              <div className="currency-input">
                <span className="currency-input__prefix">Rp</span>
                <input id={`cost-item-jumlah-${i}`} type="text" inputMode="numeric" value={formatCurrencyInput(it.amount)}
                  onChange={(e) => setAmount(i, parseCurrencyInput(e.target.value))} />
              </div>
            </div>
            <button type="button" className="btn btn-ghost btn-sm" title="Hapus baris" onClick={() => removeItem(i)}>✕</button>
          </div>
        </div>
      ))}
      <div style={{ display: "flex", gap: "var(--gap-sm)" }}>
        {typeOptions.map((t) => (
          <button key={t.value} type="button" className="btn btn-sm" onClick={() => addItem(t.value)}>
            {t.buttonLabel}
          </button>
        ))}
      </div>
      {checkResult && !checkResult.ok && (
        <div className="form-hint" style={{ color: "var(--red)", marginTop: "var(--gap-sm)" }}>{checkResult.error}</div>
      )}
    </div>
  );
}
